import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Text, Group, Button, Badge, Center, Stack, SimpleGrid, Paper,
  Tabs, ThemeIcon, Loader, Modal, Textarea, TextInput,
  Checkbox, ScrollArea, Image, Select, SegmentedControl,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconListCheck, IconServer, IconClockHour4, IconCircleCheck,
  IconAlertTriangle, IconChevronRight, IconQrcode, IconCheck,
  IconX, IconPhoto, IconHistory, IconDeviceLaptop, IconPrinter,
  IconArrowsExchange,
} from '@tabler/icons-react'
import CameraScanner from '../components/scanner/CameraScanner'
import {
  getComsets, getComsetDetail, getCheckSessions, getCheckSession,
  submitCheckSession, getModels, getAllAssetsAdmin, getSoftwareLibrary,
  saveCheckProgress, reportAssetLost, cancelCheckSession,
} from '../api/client'
import { useAuth } from '../hooks/useAuth'

// ── helpers ───────────────────────────────────────────────────────────────────
const getDueStatus = (c: any) => {
  if (!c.nextCheckDue && !c.lastCheckedAt) return { color: 'gray', key: 'NEVER' }
  if (!c.nextCheckDue) return { color: 'gray', key: 'NEVER' }
  const days = Math.ceil((new Date(c.nextCheckDue).getTime() - Date.now()) / 86400000)
  if (days < 0)  return { color: 'red',    key: 'OVERDUE', days: Math.abs(days) }
  if (days <= 7) return { color: 'orange', key: 'SOON',    days }
  return { color: 'teal', key: 'OK', days }
}

const fmtDate = (d: string | null) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—'

// ── Print Report ──────────────────────────────────────────────────────────────
// โลโก้ฝังเป็น base64 ไม่ต้องพึ่ง server
const LOGO_DATA_URL = "data:image/jpeg;base64,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"

// รองรับทั้งพิมพ์ session เดียว และหลาย session พร้อมกัน (signature ร่วม 1 ชุด)
async function printCheckReport(sessionIds: number[], assetMap: Record<number, any>) {
  if (!sessionIds.length) return

  // Load all sessions in parallel
  let sessions: any[]
  try {
    const { getCheckSession } = await import('../api/client')
    const results = await Promise.all(sessionIds.map(id => getCheckSession(id)))
    sessions = results.map(r => r.data)
  } catch { alert('Could not load session data'); return }

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  const css = `
    @page { size: A4; margin: 14mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'DM Sans','Inter',sans-serif; font-size: 11px; color: #0f172a; }
    .page-break { page-break-before: always; }

    /* Header */
    .header { display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 14px; border-bottom: 3px solid #0f172a; padding-bottom: 10px; }
    .header img { height: 48px; object-fit: contain; }
    .header-center { text-align: center; flex: 1; }
    .header-center h1 { font-size: 16px; font-weight: 700; letter-spacing: -0.3px; }
    .header-center .sub { font-size: 9px; color: #64748b; margin-top: 2px; }
    .header-right { text-align: right; min-width: 140px; }
    .header-right .ref { font-size: 9px; color: #64748b; }
    .header-right .date { font-size: 11px; font-weight: 600; }

    /* Section */
    .section { margin-bottom: 10px; }
    .section-title { background: #0f172a; color: #fff; font-weight: 700; font-size: 9px;
      text-transform: uppercase; letter-spacing: 0.5px; padding: 4px 10px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #e2e8f0; border-top: none; }
    .info-label { font-size: 9px; color: #64748b; font-weight: 600; padding: 5px 8px;
      border-bottom: 1px solid #f1f5f9; border-right: 1px solid #e2e8f0; background: #f8fafc; }
    .info-value { font-size: 10px; padding: 5px 8px; border-bottom: 1px solid #f1f5f9; }

    /* Table */
    table { width: 100%; border-collapse: collapse; }
    table.main-table { border: 1px solid #e2e8f0; }
    thead th { background: #f1f5f9; padding: 5px 7px; font-size: 8px; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.4px; color: #475569;
      border: 1px solid #e2e8f0; text-align: left; }
    tbody td { padding: 5px 7px; font-size: 10px; border: 1px solid #e2e8f0; vertical-align: middle; }
    .pass-badge { display: inline-block; background: #f0fdf4; color: #0d9488;
      font-weight: 700; padding: 1px 5px; border-radius: 3px; font-size: 8px; }
    .fail-badge { display: inline-block; background: #fef2f2; color: #dc2626;
      font-weight: 700; padding: 1px 5px; border-radius: 3px; font-size: 8px; }
    .tick { color: #0d9488; font-weight: 700; }
    .cross { color: #dc2626; font-weight: 700; }
    .dash { color: #94a3b8; }
    .striped { background: #fafafa; }

    /* Overall */
    .overall-row { display: flex; gap: 12px; align-items: center;
      padding: 8px 12px; border: 1px solid #e2e8f0; border-top: none; }
    .overall-badge { font-size: 14px; font-weight: 800; font-family: 'Inter',sans-serif; }
    .pass-color { color: #0d9488; }
    .fail-color { color: #dc2626; }
    .partial-color { color: #f59e0b; }

    /* Signatures - ย้ายมาท้าย document รวมกันทุก COMSET */
    .sig-section { margin-top: 20px; padding-top: 12px; border-top: 2px solid #0f172a; }
    .sig-title-main { font-size: 11px; font-weight: 700; margin-bottom: 12px; }
    .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .sig-box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 16px; }
    .sig-role-title { font-size: 10px; font-weight: 700; margin-bottom: 3px; }
    .sig-role-sub { font-size: 9px; color: #94a3b8; margin-bottom: 28px; }
    .sig-line { border-top: 1px solid #94a3b8; padding-top: 4px; margin-top: 12px; }
    .sig-footer { font-size: 9px; color: #64748b; }

    /* Footer */
    .doc-footer { margin-top: 16px; padding-top: 6px; border-top: 1px solid #e2e8f0;
      text-align: center; font-size: 8px; color: #94a3b8; }

    /* Summary table at start (multi-COMSET) */
    .summary-section { background: #f8fafc; border: 1px solid #e2e8f0;
      border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; }
    .summary-title { font-size: 10px; font-weight: 700; margin-bottom: 8px; color: #0f172a; }
  `

  const renderSession = (session: any, idx: number, isLast: boolean) => {
    const comset = session.comset ?? {}
    const results: any[] = session.results ?? []
    const checkedAt = session.checkedAt
      ? new Date(session.checkedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—'
    const firstResult = results[0]
    const swItems: any[]  = firstResult?.softwareResults  ?? []
    const chkItems: any[] = firstResult?.checklistResults ?? []
    const overallClass = session.overallResult === 'PASS' ? 'pass-color'
      : session.overallResult === 'FAIL' ? 'fail-color' : 'partial-color'
    const passCount = results.filter((r: any) => r.passed).length

    return `
      ${idx > 0 ? '<div class="page-break"></div>' : ''}

      <!-- Session Header (only show full header for first, simplified for subsequent) -->
      ${idx === 0 ? `
      <div class="header">
        <img src="${LOGO_DATA_URL}" alt="Logo" />
        <div class="header-center">
          <h1>Preventive Maintenance Checking Report</h1>
          <div class="sub">Asset Management System — AssetOS</div>
        </div>
        <div class="header-right">
          <div class="ref">Print Date: ${today}</div>
          ${sessions.length > 1 ? `<div class="ref">${sessions.length} COMSET Groups</div>` : `<div class="ref">Session #${session.id}</div>`}
        </div>
      </div>
      ` : `
      <div style="display:flex; justify-content:space-between; align-items:center; border-bottom: 2px solid #0f172a; padding-bottom: 8px; margin-bottom: 12px;">
        <img src="${LOGO_DATA_URL}" alt="Logo" style="height:36px;"/>
        <div style="font-size:13px;font-weight:700;">Preventive Maintenance Checking Report (cont.)</div>
        <div style="font-size:9px;color:#64748b;text-align:right;">Session #${session.id}<br>${checkedAt}</div>
      </div>
      `}

      <!-- COMSET Info -->
      <div class="section">
        <div class="section-title">${sessions.length > 1 ? `${idx + 1}. ` : '1. '}Group Information</div>
        <div class="info-grid">
          <div class="info-label">COMSET Group</div><div class="info-value"><strong>${comset.name ?? '—'}</strong></div>
          <div class="info-label">Department</div><div class="info-value">${comset.departmentCode ?? '—'}</div>
          <div class="info-label">Location</div><div class="info-value">${comset.location ?? '—'}</div>
          <div class="info-label">Checked By / Date</div><div class="info-value">${session.checkedBy ?? '—'} · ${checkedAt}</div>
        </div>
      </div>

      <!-- Equipment & SN -->
      <div class="section">
        <div class="section-title">${sessions.length > 1 ? `${idx + 1}.2 ` : '2. '}Equipment Inventory & Serial Number Verification</div>
        <table class="main-table">
          <thead>
            <tr>
              <th style="width:24px">No.</th><th>Asset Code</th><th>Asset Name</th><th>Model</th>
              <th>SN on Record</th><th>SN Scanned</th><th style="width:60px">SN Match</th>
              <th>Note</th><th style="width:46px">Result</th>
            </tr>
          </thead>
          <tbody>
            ${results.map((r: any, i: number) => {
              const asset = assetMap[r.assetId] ?? {}
              const snOk = r.snMatch === true  ? '<span class="tick">✓</span>'
                : r.snMatch === false ? '<span class="cross">✗</span>'
                : '<span class="dash">—</span>'
              return `<tr class="${i % 2 === 1 ? 'striped' : ''}">
                <td style="text-align:center">${i + 1}</td>
                <td><strong>${asset.assetCode ?? '#' + r.assetId}</strong></td>
                <td>${asset.name ?? '—'}</td>
                <td style="color:#64748b">${asset.model ?? '—'}</td>
                <td style="color:#64748b">${r.snExpected ?? '—'}</td>
                <td style="color:#64748b">${r.snScanned ?? '—'}</td>
                <td style="text-align:center">${snOk}</td>
                <td style="color:#64748b;font-size:9px">${r.note ?? ''}</td>
                <td style="text-align:center">${r.passed ? '<span class="pass-badge">PASS</span>' : '<span class="fail-badge">FAIL</span>'}</td>
              </tr>`
            }).join('')}
          </tbody>
        </table>
      </div>

      ${swItems.length > 0 ? `
      <div class="section">
        <div class="section-title">Software Inspection</div>
        <table class="main-table">
          <thead><tr>
            <th style="width:20px">✓</th><th>Software / Item</th>
            <th style="width:60px">Required</th><th style="width:80px">Version Found</th><th>Remark</th>
          </tr></thead>
          <tbody>
            ${swItems.map((sw: any, i: number) => `
              <tr class="${i%2===1?'striped':''}">
                <td style="text-align:center">${sw.found ? '<span class="tick">✓</span>' : '<span class="cross">✗</span>'}</td>
                <td>${sw.name}</td>
                <td style="color:#64748b">${sw.required ? 'Required' : 'Optional'}</td>
                <td style="color:#64748b">${sw.foundVersion ?? '—'}</td>
                <td></td>
              </tr>`).join('')}
          </tbody>
        </table>
      </div>` : ''}

      ${chkItems.length > 0 ? `
      <div class="section">
        <div class="section-title">Maintenance Checklist</div>
        <table class="main-table">
          <thead><tr>
            <th style="width:20px">✓</th><th>Checklist Item</th>
            <th style="width:60px">Required</th><th>Remark</th>
          </tr></thead>
          <tbody>
            ${chkItems.map((c: any, i: number) => `
              <tr class="${i%2===1?'striped':''}">
                <td style="text-align:center">${c.done ? '<span class="tick">✓</span>' : '<span class="cross">✗</span>'}</td>
                <td>${c.name}</td>
                <td style="color:#64748b">${c.required ? 'Required' : 'Optional'}</td>
                <td style="font-size:9px;color:#64748b">${c.note ?? ''}</td>
              </tr>`).join('')}
          </tbody>
        </table>
      </div>` : ''}

      <!-- Overall Result for this session -->
      <div class="section">
        <div class="section-title">Overall Result</div>
        <div class="overall-row">
          <div class="overall-badge ${overallClass}">${session.overallResult ?? 'PARTIAL'}</div>
          <div style="font-size:10px;color:#64748b">${passCount} / ${results.length} assets passed</div>
          ${session.note ? `<div style="font-size:9px;color:#64748b;margin-left:16px">Note: ${session.note}</div>` : ''}
        </div>
      </div>
    `
  }

  // Build full HTML
  const html = `<!DOCTYPE html><html><head>
  <meta charset="utf-8">
  <title>PM Checking Report</title>
  <style>${css}</style>
</head><body>

  ${sessions.length > 1 ? `
  <!-- Multi-COMSET Summary Table (cover page info) -->
  <div class="header">
    <img src="${LOGO_DATA_URL}" alt="Logo" />
    <div class="header-center">
      <h1>Preventive Maintenance Checking Report</h1>
      <div class="sub">Asset Management System — AssetOS</div>
    </div>
    <div class="header-right">
      <div class="ref">Print Date: ${today}</div>
      <div class="ref">${sessions.length} COMSET Groups</div>
    </div>
  </div>
  <div class="summary-section">
    <div class="summary-title">Summary — ${sessions.length} COMSET Groups</div>
    <table>
      <thead><tr>
        <th>#</th><th>COMSET Group</th><th>Department</th><th>Location</th>
        <th>Checked By</th><th>Date</th><th>Assets</th><th>Result</th>
      </tr></thead>
      <tbody>
        ${sessions.map((s: any, i: number) => {
          const checkedAt = s.checkedAt ? new Date(s.checkedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
          const overallClass = s.overallResult === 'PASS' ? 'pass-color' : s.overallResult === 'FAIL' ? 'fail-color' : 'partial-color'
          return `<tr class="${i%2===1?'striped':''}">
            <td>${i+1}</td>
            <td><strong>${s.comset?.name ?? '—'}</strong></td>
            <td>${s.comset?.departmentCode ?? '—'}</td>
            <td>${s.comset?.location ?? '—'}</td>
            <td>${s.checkedBy ?? '—'}</td>
            <td>${checkedAt}</td>
            <td style="text-align:center">${(s.results ?? []).length}</td>
            <td class="${overallClass}" style="font-weight:700">${s.overallResult ?? '—'}</td>
          </tr>`
        }).join('')}
      </tbody>
    </table>
  </div>
  <div class="page-break"></div>
  ` : ''}

  ${sessions.map((s, i) => renderSession(s, i, i === sessions.length - 1)).join('')}

  <!-- Shared Signature section — ท้ายเอกสารรวม เซ็นครั้งเดียวทุก COMSET -->
  <div class="sig-section">
    <div class="sig-title-main">Authorized Signatures${sessions.length > 1 ? ` — Applies to All ${sessions.length} COMSET Groups` : ''}</div>
    <div class="sig-grid">
      <div class="sig-box">
        <div class="sig-role-title">IT Manager</div>
        <div class="sig-role-sub">Name: ________________________________</div>
        <div class="sig-line">
          <div class="sig-footer">Signature: ________________________________</div>
          <div class="sig-footer" style="margin-top:4px">Date: __________ / __________ / __________</div>
        </div>
      </div>
      <div class="sig-box">
        <div class="sig-role-title">Head of Department</div>
        <div class="sig-role-sub">Name: ________________________________</div>
        <div class="sig-line">
          <div class="sig-footer">Signature: ________________________________</div>
          <div class="sig-footer" style="margin-top:4px">Date: __________ / __________ / __________</div>
        </div>
      </div>
    </div>
  </div>

  <div class="doc-footer">
    Generated by AssetOS •
    ${sessions.map((s: any) => `Session #${s.id}`).join(', ')} •
    ${today}
  </div>

</body></html>`

  // Blob URL → เปิด tab ใหม่แสดง print preview panel ทางขวา (แบบเดียวกับ Report page)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const tab  = window.open(url, '_blank')
  if (!tab) { alert('Please allow popups to open print preview'); return }
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
// ── main component ────────────────────────────────────────────────────────────
export default function AssetCheckingPage() {
  const { t } = useTranslation()
  const { isSmall } = useBreakpoint()
  const { username } = useAuth()

  const [comsets, setComsets] = useState<any[]>([])
  const [assetMap, setAssetMap] = useState<Record<number,any>>({})
  const [sessions, setSessions] = useState<any[]>([])
  const [selectedSessions, setSelectedSessions] = useState<Set<number>>(new Set())
  const toggleSession = (id: number) => setSelectedSessions(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const [models, setModels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<string | null>('overview')

  // Checking modal
  const [checkOpened, { open: openCheck, close: closeCheck }] = useDisclosure(false)
  const [selectedComset, setSelectedComset] = useState<any>(null)
  const [comsetDetail, setComsetDetail] = useState<any>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [assetResults, setAssetResults] = useState<Record<number, any>>({})
  const [sessionNote, setSessionNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [checklistMode, setChecklistMode] = useState<'grouped' | 'per-device'>('per-device')
  const [swMode, setSwMode] = useState<'grouped' | 'per-device'>('per-device')
  const [comsetSearch, setComsetSearch] = useState('')
  const [skipSnCheck, setSkipSnCheck] = useState(false)
  const [draftSessionId, setDraftSessionId] = useState<number | null>(null)
  const [savingProgress, setSavingProgress] = useState(false)
  const [lostAssets, setLostAssets] = useState<any[]>([])
  const [lostConfirmOpened, { open: openLostConfirm, close: closeLostConfirm }] = useDisclosure(false)
  const [cancelTarget, setCancelTarget] = useState<any>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelOpened, { open: openCancel, close: closeCancel }] = useDisclosure(false)

  const load = async () => {
    setLoading(true)
    try {
      const [c, s, m, a] = await Promise.all([getComsets(), getCheckSessions(), getModels(), getAllAssetsAdmin()])
      setComsets(c.data ?? [])
      setSessions(s.data ?? [])
      const map: Record<string, string> = {}
      ;(m.data ?? []).forEach((mod: any) => { if (mod.code && mod.imageUrl) map[mod.code] = mod.imageUrl })
      setModels(map as any)
      // Build asset map id → asset
      const amap: Record<number,any> = {}
      ;(a.data ?? []).forEach((asset: any) => {
        amap[asset.id] = { ...asset, imageUrl: asset.imageUrl || (asset.model ? map[asset.model] : undefined) }
      })
      setAssetMap(amap)
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  // ── Open checking modal ──────────────────────────────────────────────────────
  const startChecking = async (comset: any, resumeSessionId?: number) => {
    setSelectedComset(comset)
    setSessionNote('')
    setAssetResults({})
    // ใช้ค่าจาก COMSET config (จะถูก override โดย draft ถ้า resume)
    setSkipSnCheck(comset.skipSnCheck ?? false)
    setChecklistMode((comset.checklistMode ?? 'per-device') as 'grouped' | 'per-device')
    setDraftSessionId(resumeSessionId ?? null)
    openCheck()
    setDetailLoading(true)
    try {
      const [r, swLibRes] = await Promise.all([getComsetDetail(comset.id), getSoftwareLibrary()])
      setComsetDetail(r.data)
      const swMap: Record<number, boolean> = {}
      ;(swLibRes.data ?? []).forEach((sw: any) => { swMap[sw.id] = sw.checkVersion ?? true })
      const ids: number[] = r.data.comset.assetIds ?? []
      const template = r.data.template
      const initResults: Record<number, any> = {}
      ids.forEach(id => {
        initResults[id] = {
          assetId: id, snScanned: '', snExpected: '', checkState: 'unchecked', notFound: false,
          softwareResults: (template?.softwareItems ?? []).map((s: any) => ({
            softwareId: s.softwareId, name: s.name, required: s.required,
            checkVersion: swMap[s.softwareId] ?? s.checkVersion ?? true,
            found: false, foundVersion: '',
          })),
          checklistResults: (template?.checklistItems ?? []).map((c: any) => ({
            checklistItemId: c.checklistItemId, name: c.name, required: c.required,
            requiresNote: c.requiresNote ?? false,
            done: false, note: '',
          })),
          passed: false,
        }
      })

      // ── Restore draft session if resuming ──────────────────────────────
      if (resumeSessionId) {
        try {
          const draft = await getCheckSession(resumeSessionId)
          const draftData = draft.data
          if (draftData?.note) setSessionNote(draftData.note)
          if (draftData?.skipSnCheck) setSkipSnCheck(true)
          ;(draftData?.results ?? []).forEach((r: any) => {
            if (initResults[r.assetId]) {
              // checkState carries forward if present (new format); otherwise derive
              // from the legacy notFound boolean so old drafts still restore correctly.
              const restoredCheckState = r.checkState
                ?? (r.notFound ? 'notfound' : (r.snScanned || r.note || r.softwareResults?.some((s: any) => s.found) || r.checklistResults?.some((c: any) => c.done) ? 'found' : 'unchecked'))
              initResults[r.assetId] = {
                ...initResults[r.assetId],
                checkState:       restoredCheckState,
                notFound:         restoredCheckState === 'notfound',
                snScanned:        r.snScanned        ?? '',
                snExpected:       r.snExpected        ?? initResults[r.assetId].snExpected,
                sameAsRecord:     r.sameAsRecord,
                note:             r.note             ?? '',
                softwareResults:  r.softwareResults?.length  ? r.softwareResults  : initResults[r.assetId].softwareResults,
                checklistResults: r.checklistResults?.length ? r.checklistResults : initResults[r.assetId].checklistResults,
              }
            }
          })
          notifications.show({ color: 'orange', message: `Resumed from draft #${resumeSessionId} — ${(draftData?.results ?? []).length} results restored` })
        } catch {
          notifications.show({ color: 'yellow', message: 'Could not restore draft — starting fresh' })
        }
      }

      setAssetResults(initResults)
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setDetailLoading(false) }
  }

  // ── Submit session ───────────────────────────────────────────────────────────
  // ── Save Progress (draft) ─────────────────────────────────────────────────
  // ตรวจสอบว่า asset นี้ถูก "ตรวจแล้วจริง" หรือยัง (ไม่ใช่แค่ initial empty state)
  const isAssetChecked = (r: any): boolean => {
    if (r.checkState === 'found' || r.checkState === 'notfound') return true
    if (r.notFound) return true
    if (r.sameAsRecord === false) return true  // user explicitly said SN is different → needs scan
    if (r.snScanned?.trim()) return true
    if (r.softwareResults?.some((s: any) => s.found)) return true
    if (r.checklistResults?.some((c: any) => c.done)) return true
    if (r.note?.trim()) return true
    return false
  }

  const handleSaveProgress = async () => {
    if (!selectedComset) return
    setSavingProgress(true)
    try {
      // ── นับเฉพาะ asset ที่ "ตรวจแล้วจริง" เท่านั้น ไม่ใช่ทุก asset ใน initial state ──
      const results = Object.entries(assetResults)
        .filter(([_, r]: [string, any]) => isAssetChecked(r))
        .map(([assetId, r]: [string, any]) => {
          const snExpected = r.snExpected?.trim() || ''
          const snScanned  = r.snScanned?.trim()  || ''
          return { ...r, snExpected: snExpected || undefined, snScanned: snScanned || undefined }
        })
      const res = await saveCheckProgress(
        { comsetId: selectedComset.id, results, note: sessionNote || undefined, skipSnCheck },
        draftSessionId ?? undefined
      )
      setDraftSessionId(res.data.id)
      notifications.show({ color: 'teal', title: 'Progress Saved', message: `Draft session #${res.data.id} saved — ${results.length} of ${Object.keys(assetResults).length} assets checked` })
    } catch {
      notifications.show({ color: 'red', message: 'Failed to save progress' })
    } finally { setSavingProgress(false) }
  }

  // ── Final Submit ──────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedComset) return

    const uncheckedCount = Object.values(assetResults).filter((r: any) => !isAssetChecked(r)).length
    const notFoundCount  = Object.values(assetResults).filter((r: any) => r.notFound).length

    // ── ถามยืนยันก่อน ถ้ามี unchecked หรือ not-found ──
    if (uncheckedCount > 0 || notFoundCount > 0) {
      modals.openConfirmModal({
        title: <Text fw={700}>⚠️ Confirm Before Saving</Text>,
        children: (
          <Stack gap={8}>
            {uncheckedCount > 0 && (
              <Text size="sm">
                <b>{uncheckedCount}</b> asset(s) were not checked at all (no SN scan, software, or checklist entered).
              </Text>
            )}
            {notFoundCount > 0 && (
              <Text size="sm" c="red">
                {t("checking.notFoundBody", "{{count}} asset(s) are marked Not Found — these will be reported as missing/lost.", { count: notFoundCount })}
              </Text>
            )}
            <Text size="sm" c="dimmed">Do you want to continue saving this check session?</Text>
          </Stack>
        ),
        labels: { confirm: 'Yes, Save Anyway', cancel: 'Go Back' },
        confirmProps: { color: 'orange' },
        onConfirm: () => doSubmit(),
      })
      return
    }

    doSubmit()
  }

  const doSubmit = async () => {
    if (!selectedComset) return
    setSubmitting(true)
    try {
      const results = Object.values(assetResults).map((r: any) => {
        const snExpected = r.snExpected?.trim() || ''
        const snScanned  = r.snScanned?.trim()  || ''
        return { ...r, snExpected: snExpected || undefined, snScanned: snScanned || undefined }
      })
      await submitCheckSession({ comsetId: selectedComset.id, results, note: sessionNote || undefined, skipSnCheck })
      notifications.show({ color: 'teal', message: 'Check session saved successfully' })
      closeCheck()
      load()

      // ── ถ้ามี asset ที่ mark "Not Found" → เสนอรายงาน Lost ──
      const notFoundList = Object.values(assetResults)
        .filter((r: any) => r.notFound)
        .map((r: any) => ({ ...r, asset: assetMap[r.assetId] }))
      if (notFoundList.length > 0) {
        setLostAssets(notFoundList)
        openLostConfirm()
      }
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
    finally { setSubmitting(false) }
  }

  const updateResult = (assetId: number, patch: any) => {
    setAssetResults(p => ({ ...p, [assetId]: { ...p[assetId], ...patch } }))
  }
  const updateSw = (assetId: number, swId: number, patch: any) => {
    setAssetResults(p => ({
      ...p,
      [assetId]: {
        ...p[assetId],
        softwareResults: p[assetId].softwareResults.map((s: any) =>
          s.softwareId === swId ? { ...s, ...patch } : s
        ),
      },
    }))
  }
  const updateChk = (assetId: number, chkId: number, patch: any) => {
    setAssetResults(p => ({
      ...p,
      [assetId]: {
        ...p[assetId],
        checklistResults: p[assetId].checklistResults.map((c: any) =>
          c.checklistItemId === chkId ? { ...c, ...patch } : c
        ),
      },
    }))
  }

  // ── Summary counts ────────────────────────────────────────────────────────────
  const ok      = comsets.filter(c => getDueStatus(c).key === 'OK').length
  const overdue = comsets.filter(c => getDueStatus(c).key === 'OVERDUE').length
  const never   = comsets.filter(c => getDueStatus(c).key === 'NEVER').length

  const modelImageMap: Record<string, string> = models as any

  return (
    <Box p={isSmall ? 16 : 32}>
      {/* Header */}
      <Group justify="space-between" mb="xl" align="flex-start">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, color: '#0f172a' }}>
            {t('checking.title', 'Asset Checking')}
          </Text>
          <Text size="sm" c="dimmed">{t('checking.subtitle', 'Inspect assets by COMSET — SN, Software and Checklist')}</Text>
        </Box>
      </Group>

      {/* Summary Cards */}
      <SimpleGrid cols={isSmall ? 2 : 4} mb="xl">
        {[
          { labelKey: 'checking.totalComsets', count: comsets.length, color: '#6366f1', icon: IconServer },
          { labelKey: 'checking.checked',      count: ok,             color: '#10b981', icon: IconCircleCheck },
          { labelKey: 'checking.overdue',      count: overdue,        color: '#ef4444', icon: IconAlertTriangle },
          { labelKey: 'checking.neverChecked', count: never,          color: '#94a3b8', icon: IconClockHour4 },
        ].map(s => (
          <Paper key={s.labelKey} withBorder radius="lg" p="md" style={{ borderLeft: `4px solid ${s.color}` }}>
            <Group gap={8} mb={4}>
              <ThemeIcon size="sm" radius="md" variant="light"
                color={s.color === '#6366f1' ? 'violet' : s.color === '#10b981' ? 'teal' : s.color === '#ef4444' ? 'red' : 'gray'}>
                <s.icon size={13} />
              </ThemeIcon>
              <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                {t(s.labelKey)}
              </Text>
            </Group>
            <Text size="xl" fw={800} style={{ color: s.color }}>{s.count}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab} radius="md">
        <Tabs.List mb="md">
          <Tabs.Tab value="overview" leftSection={<IconListCheck size={14} />}>
            {t('checking.overview', 'COMSET Overview')}
          </Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
            {t('checking.history', 'Check History')}
            {sessions.length > 0 && <Badge size="xs" color="gray" variant="light" ml={6}>{sessions.length}</Badge>}
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Tab: Overview ── */}
        <Tabs.Panel value="overview">
          {loading ? <Center p="xl"><Loader color="teal" /></Center> : (
            <Stack gap="sm">
              {/* Search box */}
              <TextInput
                placeholder={t("checking.searchComsetPlaceholder","🔍 Search COMSET by name or location...")}
                value={comsetSearch}
                onChange={e => setComsetSearch(e.target.value)}
                leftSection={<IconQrcode size={14} />}
                radius="md"
                mb={4}
              />
              {comsets.filter(c =>
                !comsetSearch.trim() ||
                c.name?.toLowerCase().includes(comsetSearch.toLowerCase()) ||
                c.location?.toLowerCase().includes(comsetSearch.toLowerCase())
              ).length === 0 ? (
                <Center p="xl">
                  <Stack align="center" gap={8}>
                    <IconServer size={40} color="#cbd5e1" />
                    <Text size="sm" c="dimmed">
                      {comsetSearch ? `No COMSET matching "${comsetSearch}"` : t('checking.noComset')}
                    </Text>
                  </Stack>
                </Center>
              ) : comsets.filter(c =>
                  !comsetSearch.trim() ||
                  c.name?.toLowerCase().includes(comsetSearch.toLowerCase()) ||
                  c.location?.toLowerCase().includes(comsetSearch.toLowerCase())
                ).map(comset => {
                const due = getDueStatus(comset)
                const borderColor = due.key === 'OVERDUE' ? '#fca5a5' : '#e2e8f0'
                const dueLabel = due.key === 'NEVER'   ? t('checking.neverChecked')
                  : due.key === 'OVERDUE' ? t('checking.dueOverdue', { days: due.days })
                  : due.key === 'SOON'    ? t('checking.dueSoon',    { days: due.days })
                  : t('checking.dueOk', { days: due.days })
                const draftSession = sessions.find((s: any) => s.status === 'IN_PROGRESS' && s.comsetId === comset.id)
                return (
                  <Paper key={comset.id} withBorder radius="lg" p="md"
                    style={{
                      border: draftSession ? '1.5px solid #f97316' : `1.5px solid ${borderColor}`,
                      background: draftSession ? '#fff7ed' : undefined,
                      cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
                    }}
                    onClick={(e) => { e.preventDefault(); startChecking(comset, draftSession?.id) }}>
                    <Group justify="space-between" wrap="wrap" gap="sm">
                      <Group gap={12}>
                        <ThemeIcon size={42} radius="xl" variant="light"
                          color={draftSession ? 'orange' : due.key === 'OK' || due.key === 'SOON' ? 'teal' : due.key === 'OVERDUE' ? 'red' : 'gray'}>
                          {draftSession ? <IconAlertTriangle size={20} /> : <IconServer size={20} />}
                        </ThemeIcon>
                        <Box>
                          <Group gap={6}>
                            <Text fw={600} size="sm">{comset.name}</Text>
                            {draftSession && <Badge size="xs" color="orange" variant="filled">{t("common.inProgress","IN PROGRESS")}</Badge>}
                          </Group>
                          <Group gap={6}>
                            <Text size="xs" c="dimmed">📍 {comset.location || '—'}</Text>
                            <Text size="xs" c="dimmed">·</Text>
                            <Text size="xs" c="dimmed">{(comset.assetIds ?? []).length} {t('comset.assets', 'assets')}</Text>
                            {comset.checkFrequency && (
                              <>
                                <Text size="xs" c="dimmed">·</Text>
                                <Text size="xs" c="dimmed" tt="capitalize">{comset.checkFrequency.toLowerCase()}</Text>
                              </>
                            )}
                          </Group>
                        </Box>
                      </Group>
                      <Group gap={8}>
                        {draftSession ? (
                          <>
                            <Text size="xs" c="orange" fw={500}>
                              {(draftSession.results ?? []).length} / {(comset.assetIds ?? []).length} checked
                            </Text>
                            <Button size="xs" color="red" variant="subtle"
                              leftSection={<IconX size={12} />}
                              onClick={(e) => {
                                e.stopPropagation()
                                setCancelTarget(draftSession); setCancelReason(''); openCancel()
                              }}>
                              {t('common.cancel', 'Cancel')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Box ta="right">
                              <Text size="xs" c="dimmed">
                                {t('checking.lastChecked')}: {fmtDate(comset.lastCheckedAt)}
                              </Text>
                              <Text size="xs" c={due.key === 'OVERDUE' ? 'red' : 'dimmed'}>
                                {t('checking.nextDue')}: {comset.nextCheckDue ? fmtDate(comset.nextCheckDue) : '—'}
                              </Text>
                            </Box>
                            <Badge color={due.color} variant="light" size="sm">{dueLabel}</Badge>
                          </>
                        )}
                        <IconChevronRight size={16} color="#94a3b8" />
                      </Group>
                    </Group>
                  </Paper>
                )
              })}
            </Stack>
          )}
        </Tabs.Panel>

        {/* ── Tab: History ── */}
        <Tabs.Panel value="history">
          {loading ? <Center p="xl"><Loader color="teal" /></Center> : sessions.length === 0 ? (
            <Center p="xl">
              <Stack align="center" gap={8}>
                <IconHistory size={40} color="#cbd5e1" />
                <Text size="sm" c="dimmed">{t('checking.noHistory')}</Text>
              </Stack>
            </Center>
          ) : (
            <Stack gap="md">

              {/* ── IN_PROGRESS Drafts Section ── */}
              {sessions.filter((s: any) => s.status === 'IN_PROGRESS').length > 0 && (
                <Box>
                  <Group gap={8} mb={10}>
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#fb923c', animation: 'pulse 2s infinite' }} />
                    <Text size="xs" fw={700} c="orange" tt="uppercase" style={{ letterSpacing: '0.6px' }}>
                      In Progress — Drafts
                    </Text>
                    <Badge size="xs" color="orange" variant="outline">
                      {sessions.filter((s: any) => s.status === 'IN_PROGRESS').length}
                    </Badge>
                  </Group>

                  <Stack gap={8}>
                    {sessions.filter((s: any) => s.status === 'IN_PROGRESS').map((s: any) => {
                      const resultCount = (s.results ?? []).length
                      const totalCount = s.comset?.assetIds?.length ?? '?'
                      const pct = typeof totalCount === 'number' && totalCount > 0
                        ? Math.round(resultCount / totalCount * 100) : null
                      return (
                        <Paper key={s.id} withBorder radius="lg" p="md"
                          style={{ borderColor: '#f97316', background: '#fff7ed', borderWidth: 1.5 }}>
                          <Group justify="space-between" wrap="nowrap">
                            <Group gap={12} style={{ minWidth: 0, flex: 1 }}>
                              <Box style={{ background: '#fed7aa', borderRadius: 10, padding: '8px 10px', flexShrink: 0 }}>
                                <IconAlertTriangle size={18} color="#c2410c" />
                              </Box>
                              <Box style={{ minWidth: 0, flex: 1 }}>
                                <Group gap={8} mb={2}>
                                  <Text size="sm" fw={700}>{s.comset?.name ?? `COMSET #${s.comsetId}`}</Text>
                                  <Badge size="xs" color="orange" variant="filled">{t("common.inProgress","IN PROGRESS")}</Badge>
                                  {s.skipSnCheck && <Badge size="xs" color="gray" variant="outline">{t("checking.skipSN","Skip SN")}</Badge>}
                                </Group>
                                <Text size="xs" c="dimmed">
                                  Saved by {s.checkedBy} · {fmtDate(s.updatedAt ?? s.checkedAt)}
                                </Text>
                                {/* Progress bar */}
                                <Box mt={6}>
                                  <Group justify="space-between" mb={2}>
                                    <Text size="xs" c="orange" fw={500}>
                                      {resultCount} / {totalCount} assets checked
                                      {pct !== null ? ` (${pct}%)` : ''}
                                    </Text>
                                  </Group>
                                  <Box style={{ background: '#fed7aa', borderRadius: 99, height: 4, width: '100%' }}>
                                    {pct !== null && (
                                      <Box style={{ background: '#f97316', borderRadius: 99, height: 4, width: `${pct}%` }} />
                                    )}
                                  </Box>
                                </Box>
                                {/* Investigation reminder for not-found assets */}
                                {((s.results ?? []).filter((r: any) => r.notFound).length > 0) && (
                                  <Box mt={6} style={{ background: '#fef2f2', borderRadius: 6, padding: '6px 10px', border: '1px solid #fca5a5' }}>
                                    <Text size="xs" c="red" fw={600}>
                                      🔍 {(s.results ?? []).filter((r: any) => r.notFound).length} asset(s) Not Found — investigation required before Submit
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      Add investigation findings/attach documents in Remarks before clicking Save Check Results
                                    </Text>
                                  </Box>
                                )}
                                {s.note && <Text size="xs" c="dimmed" mt={4}>Note: {s.note}</Text>}
                              </Box>
                            </Group>
                            <Stack gap={6} ml={12} style={{ flexShrink: 0 }}>
                              <Button size="xs" color="orange" variant="filled"
                                leftSection={<IconArrowsExchange size={13} />}
                                onClick={() => {
                                  const comset = comsets.find((c: any) => c.id === s.comsetId)
                                  if (comset) startChecking(comset, s.id)
                                }}>
                                {t("checking.resume","Resume")}
                              </Button>
                              <Button size="xs" color="red" variant="subtle"
                                leftSection={<IconX size={13} />}
                                onClick={() => { setCancelTarget(s); setCancelReason(''); openCancel() }}>
                                {t('common.cancel', 'Cancel')}
                              </Button>
                            </Stack>
                          </Group>
                        </Paper>
                      )
                    })}
                  </Stack>
                </Box>
              )}

              {/* ── Completed Sessions Section ── */}
              {sessions.filter((s: any) => s.status !== 'IN_PROGRESS').length > 0 && (
                <Box>
                  <Group justify="space-between" mb={10}>
                    <Group gap={8}>
                      <Box style={{ width: 8, height: 8, borderRadius: '50%', background: '#0d9488' }} />
                      <Text size="xs" fw={700} c="teal" tt="uppercase" style={{ letterSpacing: '0.6px' }}>
                        {t("checking.completedSessions","Completed Sessions")}
                      </Text>
                      <Badge size="xs" color="teal" variant="outline">
                        {sessions.filter((s: any) => s.status !== 'IN_PROGRESS').length}
                      </Badge>
                    </Group>
                    <Group gap={8}>
                      <Checkbox
                        size="xs"
                        label={<Text size="xs">{t("common.selectAll","Select All")}</Text>}
                        checked={selectedSessions.size === sessions.filter((s: any) => s.status !== 'IN_PROGRESS').length && sessions.filter((s: any) => s.status !== 'IN_PROGRESS').length > 0}
                        indeterminate={selectedSessions.size > 0 && selectedSessions.size < sessions.filter((s: any) => s.status !== 'IN_PROGRESS').length}
                        onChange={() => {
                          const completed = sessions.filter((s: any) => s.status !== 'IN_PROGRESS')
                          if (selectedSessions.size === completed.length) setSelectedSessions(new Set())
                          else setSelectedSessions(new Set(completed.map((s: any) => s.id)))
                        }}
                      />
                      {selectedSessions.size > 0 && (
                        <Button size="xs" color="teal" variant="filled"
                          leftSection={<IconPrinter size={13} />}
                          onClick={() => printCheckReport(Array.from(selectedSessions), assetMap)}>
                          {t('checking.printReportsTogether', { count: selectedSessions.size })}
                        </Button>
                      )}
                    </Group>
                  </Group>

                  <Stack gap={8}>
                    {sessions.filter((s: any) => s.status !== 'IN_PROGRESS').map((s: any) => (
                      <Paper key={s.id} withBorder radius="lg" p="md"
                        style={{ borderColor: selectedSessions.has(s.id) ? '#0d9488' : undefined,
                                 background: selectedSessions.has(s.id) ? '#f0fdf4' : undefined }}>
                        <Group justify="space-between" wrap="wrap">
                          <Group gap={10}>
                            <Checkbox
                              checked={selectedSessions.has(s.id)}
                              onChange={() => toggleSession(s.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <ThemeIcon size={36} radius="xl" variant="light"
                              color={s.overallResult === 'PASS' ? 'teal' : s.overallResult === 'FAIL' ? 'red' : 'orange'}>
                              {s.overallResult === 'PASS' ? <IconCheck size={16} /> : s.overallResult === 'FAIL' ? <IconX size={16} /> : <IconAlertTriangle size={16} />}
                            </ThemeIcon>
                            <Box>
                              <Group gap={6} mb={2}>
                                <Text size="sm" fw={600}>{s.comset?.name ?? `COMSET #${s.comsetId}`}</Text>
                                {s.skipSnCheck && <Badge size="xs" color="gray" variant="outline">{t("checking.skipSN","Skip SN")}</Badge>}
                              </Group>
                              <Text size="xs" c="dimmed">
                                Checked by {s.checkedBy} · {fmtDate(s.checkedAt)} · {(s.results ?? []).length} assets
                              </Text>
                            </Box>
                          </Group>
                          <Group gap={8}>
                            <Badge color={s.overallResult === 'PASS' ? 'teal' : s.overallResult === 'FAIL' ? 'red' : 'orange'}
                              variant="light" size="sm">{s.overallResult}</Badge>
                            <Button size="xs" variant="light" color="gray"
                              leftSection={<IconPrinter size={13} />}
                              onClick={() => printCheckReport([s.id], assetMap)}>
                              {t('common.print', 'Print')}
                            </Button>
                          </Group>
                        </Group>
                        {s.note && <Text size="xs" c="dimmed" mt={4}>Note: {s.note}</Text>}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}

            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* ── Checking Modal ── */}
      <Modal opened={checkOpened} onClose={closeCheck} size="xl" radius="lg" scrollAreaComponent={ScrollArea.Autosize}
        title={
          <Group gap={8}>
            <IconDeviceLaptop size={18} color="#0d9488" />
            <Text fw={600}>Checking: {selectedComset?.name}</Text>
            <Badge color="teal" variant="light" size="sm">📍 {selectedComset?.location}</Badge>
            {(() => {
              const checkedNow = Object.values(assetResults).filter((r: any) => isAssetChecked(r)).length
              const totalNow = Object.keys(assetResults).length
              return totalNow > 0 ? (
                <Badge color={checkedNow === totalNow ? 'teal' : 'orange'} variant="filled" size="sm">
                  {checkedNow} / {totalNow} checked
                </Badge>
              ) : null
            })()}
          </Group>
        }>
        {detailLoading ? <Center p="xl"><Loader color="teal" /></Center> : (() => {
          const entries = Object.entries(assetResults) as [string, any][]
          const firstResult = entries[0]?.[1]
          const hasSw  = (firstResult?.softwareResults ?? []).length > 0
          const hasChk = (firstResult?.checklistResults ?? []).length > 0

          return (
            <Stack gap="md">
              {/* ── Top controls: Skip SN, Grouped Software, Grouped Checklist ── */}
              <Group justify="space-between" wrap="wrap" gap={8}
                style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                <Group gap={12} wrap="wrap">
                  <Checkbox
                    size="xs"
                    label={<Text size="xs" c={skipSnCheck ? 'dimmed' : 'dark'}>{t('checking.snSkipLabel')}</Text>}
                    checked={skipSnCheck}
                    onChange={e => setSkipSnCheck(e.currentTarget.checked)}
                  />
                  {skipSnCheck && <Badge size="xs" color="orange" variant="light">{t('checking.snSkipBadge')}</Badge>}
                </Group>
                <Group gap={12} wrap="wrap">
                  {hasSw && (
                    <Group gap={4}>
                      <Text size="xs" c="dimmed">Software:</Text>
                      <SegmentedControl size="xs" value={swMode}
                        onChange={v => setSwMode(v as 'grouped' | 'per-device')}
                        data={[
                          { value: 'per-device', label: 'Per Device' },
                          { value: 'grouped',    label: 'Same for All' },
                        ]}
                      />
                    </Group>
                  )}
                  {hasChk && (
                    <Group gap={4}>
                      <Text size="xs" c="dimmed">Checklist:</Text>
                      <SegmentedControl size="xs" value={checklistMode}
                        onChange={v => setChecklistMode(v as 'grouped' | 'per-device')}
                        data={[
                          { value: 'per-device', label: 'Per Device' },
                          { value: 'grouped',    label: 'Same for All' },
                        ]}
                      />
                    </Group>
                  )}
                </Group>
              </Group>

              {/* ── Grouped Software panel — only shown when Software mode = grouped ── */}
              {hasSw && swMode === 'grouped' && (
                <Paper withBorder radius="md" p="sm" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <Text size="xs" c="teal" fw={600} mb={6}>Software — applies to all {entries.length} devices</Text>
                  <Stack gap={4}>
                    {firstResult.softwareResults.map((sw: any) => (
                      <Group key={sw.softwareId} gap={8} wrap="nowrap" justify="space-between">
                        <Checkbox size="xs" checked={sw.found}
                          onChange={e => entries.forEach(([idStr]) => updateSw(Number(idStr), sw.softwareId, { found: e.currentTarget.checked }))}
                          label={<Group gap={6}><Text size="sm">{sw.name}</Text>{sw.required && <Badge size="xs" color="red" variant="outline">{t("common.required","Required")}</Badge>}</Group>}
                        />
                        {sw.found && sw.checkVersion && (
                          <TextInput size="xs" placeholder={t("checking.versionPlaceholder","version")} style={{ width: 110, flexShrink: 0 }}
                            value={sw.foundVersion || ''}
                            onChange={e => entries.forEach(([idStr]) => updateSw(Number(idStr), sw.softwareId, { foundVersion: e.target.value }))} />
                        )}
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* ── Grouped Checklist panel — only shown when Checklist mode = grouped ── */}
              {hasChk && checklistMode === 'grouped' && (
                <Paper withBorder radius="md" p="sm" style={{ background: '#fdf4ff', border: '1px solid #f0abfc' }}>
                  <Text size="xs" c="grape" fw={600} mb={6}>Checklist — applies to all {entries.length} devices</Text>
                  <Stack gap={4}>
                    {firstResult.checklistResults.map((chk: any) => (
                      <Group key={chk.checklistItemId} gap={8} wrap="nowrap" justify="space-between">
                        <Checkbox size="xs" checked={chk.done}
                          onChange={e => entries.forEach(([idStr]) => updateChk(Number(idStr), chk.checklistItemId, { done: e.currentTarget.checked }))}
                          label={<Group gap={6}><Text size="sm">{chk.name}</Text>{chk.required && <Badge size="xs" color="orange" variant="outline">{t("common.required","Required")}</Badge>}</Group>}
                        />
                        {chk.requiresNote && (
                          <TextInput size="xs" placeholder={t("checking.remarkPlaceholder","Remark...")} style={{ width: 160, flexShrink: 0 }}
                            value={chk.note || ''}
                            onChange={e => entries.forEach(([idStr]) => updateChk(Number(idStr), chk.checklistItemId, { note: e.target.value }))} />
                        )}
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* ── Unified per-asset card list ── */}
              <Stack gap={8}>
                {entries.map(([assetIdStr, result]) => {
                  const assetId = Number(assetIdStr)
                  const asset = assetMap[assetId]
                  const checkState: 'unchecked' | 'found' | 'notfound' =
                    result.checkState ?? (result.notFound ? 'notfound' : 'unchecked')
                  const isUnchecked = checkState === 'unchecked'
                  const isNotFound  = checkState === 'notfound'
                  const sameAsRecord = result.sameAsRecord !== false
                  const snMatch = !skipSnCheck && !sameAsRecord && result.snExpected && result.snScanned
                    ? result.snExpected.toLowerCase() === result.snScanned.toLowerCase()
                    : null

                  const setCheckState = (next: 'unchecked' | 'found' | 'notfound') => {
                    updateResult(assetId, {
                      checkState: next,
                      notFound: next === 'notfound',
                      ...(next === 'notfound' ? { snScanned: '' } : {}),
                    })
                  }

                  return (
                    <Paper key={assetId} withBorder radius="md" p="sm"
                      style={{
                        border: isNotFound ? '1.5px solid #f97316' : isUnchecked ? '1px dashed #cbd5e1' : '1px solid #e2e8f0',
                        background: isNotFound ? '#fff7ed' : isUnchecked ? '#fafafa' : undefined,
                      }}>
                      {/* ── Card header: asset identity + 3-state toggle ── */}
                      <Group gap={10} mb={8} justify="space-between" wrap="nowrap">
                        <Group gap={8} style={{ minWidth: 0 }}>
                          {asset?.imageUrl
                            ? <Image src={asset.imageUrl} w={32} h={32} radius="sm" fit="cover" style={{ opacity: isUnchecked ? 0.6 : 1, flexShrink: 0 }} />
                            : <ThemeIcon size={32} radius="md" variant="light" color={isNotFound ? 'orange' : isUnchecked ? 'gray' : 'blue'}>
                                <IconDeviceLaptop size={15} />
                              </ThemeIcon>
                          }
                          <Box style={{ minWidth: 0 }}>
                            <Text size="sm" fw={700} c={isNotFound ? 'orange' : isUnchecked ? 'dimmed' : undefined} truncate>
                              {asset?.assetCode ?? `#${assetId}`}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>{asset?.name}</Text>
                          </Box>
                        </Group>
                        <SegmentedControl
                          size="xs"
                          value={checkState}
                          onChange={v => setCheckState(v as any)}
                          data={[
                            { value: 'unchecked', label: '— Unchecked' },
                            { value: 'found',     label: '✓ Found' },
                            { value: 'notfound',  label: '⚠ Not Found' },
                          ]}
                        />
                      </Group>

                      {isUnchecked && (
                        <Text size="xs" c="dimmed" fs="italic">Not yet checked — select Found or Not Found above</Text>
                      )}

                      {isNotFound && (
                        <Box style={{ background: '#fed7aa', borderRadius: 8, padding: '6px 10px' }}>
                          <Group gap={8} wrap="nowrap">
                            <Text size="xs" c="orange" fw={500} style={{ flexShrink: 0 }}>{t("checking.willBeReportedFail","⚠️ Will be reported FAIL —")}</Text>
                            <TextInput size="xs" placeholder={t("checking.lastSeenNotePlaceholder","Note (optional): last seen location, etc.")}
                              style={{ flex: 1 }}
                              value={result.note || ''}
                              onChange={e => updateResult(assetId, { note: e.target.value })} />
                          </Group>
                        </Box>
                      )}

                      {checkState === 'found' && (
                        <Stack gap={6}>
                          {/* ── SN row (inline, single line) ── */}
                          {!skipSnCheck && (
                            <Group gap={8} wrap="nowrap" align="center">
                              <Checkbox size="xs" checked={sameAsRecord}
                                onChange={e => updateResult(assetId, {
                                  sameAsRecord: e.currentTarget.checked,
                                  ...(e.currentTarget.checked ? { snScanned: '' } : { snExpected: result.snExpected || asset?.serialNumber || '' }),
                                })}
                                label={<Text size="xs">Same SN as record ({asset?.serialNumber ?? '—'})</Text>}
                                style={{ flexShrink: 0 }}
                              />
                              {!sameAsRecord && (
                                <>
                                  <TextInput size="xs" placeholder={t("checking.scanOrTypeNewSN","Scan or type new SN")} style={{ flex: 1, minWidth: 140 }}
                                    leftSection={<IconQrcode size={12} />}
                                    value={result.snScanned}
                                    onChange={e => updateResult(assetId, { snScanned: e.target.value })}
                                    styles={{ input: { borderColor: snMatch === true ? '#10b981' : snMatch === false ? '#ef4444' : undefined } }}
                                    rightSection={<CameraScanner onScanned={(val) => updateResult(assetId, { snScanned: val })} size="xs" />}
                                  />
                                  {snMatch === true  && <Badge size="xs" color="teal" variant="light" style={{ flexShrink: 0 }}>{t('checking.snMatch')}</Badge>}
                                  {snMatch === false && <Badge size="xs" color="red"  variant="light" style={{ flexShrink: 0 }}>{t('checking.snMismatch')}</Badge>}
                                </>
                              )}
                            </Group>
                          )}

                          {/* ── Software row(s) — only when per-device mode ── */}
                          {hasSw && swMode === 'per-device' && (result.softwareResults ?? []).map((sw: any) => (
                            <Group key={sw.softwareId} gap={8} wrap="nowrap" justify="space-between">
                              <Checkbox size="xs" checked={sw.found}
                                onChange={e => updateSw(assetId, sw.softwareId, { found: e.currentTarget.checked })}
                                label={<Group gap={6}><Text size="sm">{sw.name}</Text>{sw.required && <Badge size="xs" color="red" variant="outline">{t("common.required","Required")}</Badge>}</Group>}
                              />
                              {sw.found && sw.checkVersion && (
                                <TextInput size="xs" placeholder={t("checking.versionPlaceholder","version")} style={{ width: 110, flexShrink: 0 }}
                                  value={sw.foundVersion || ''}
                                  onChange={e => updateSw(assetId, sw.softwareId, { foundVersion: e.target.value })} />
                              )}
                            </Group>
                          ))}

                          {/* ── Checklist row(s) — only when per-device mode, remark inline ── */}
                          {hasChk && checklistMode === 'per-device' && (result.checklistResults ?? []).map((chk: any) => (
                            <Group key={chk.checklistItemId} gap={8} wrap="nowrap" justify="space-between">
                              <Checkbox size="xs" checked={chk.done}
                                onChange={e => updateChk(assetId, chk.checklistItemId, { done: e.currentTarget.checked })}
                                label={<Group gap={6}><Text size="sm">{chk.name}</Text>{chk.required && <Badge size="xs" color="orange" variant="outline">{t("common.required","Required")}</Badge>}</Group>}
                              />
                              {chk.requiresNote && (
                                <TextInput size="xs" placeholder={t("checking.remarkPlaceholder","Remark...")} style={{ width: 160, flexShrink: 0 }}
                                  value={chk.note || ''}
                                  onChange={e => updateChk(assetId, chk.checklistItemId, { note: e.target.value })} />
                              )}
                            </Group>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  )
                })}
              </Stack>

              {/* Session note */}
              <Textarea label={t("checking.sessionNoteOptional","Session Note (optional)")} placeholder={t("checking.sessionNotePlaceholder","Additional notes for this check session")}
                minRows={2} value={sessionNote} onChange={e => setSessionNote(e.target.value)} />

              {draftSessionId && (
                <Text size="xs" c="orange">
                  📝 Draft #{draftSessionId} — {t('checking.savePartialHint')}
                </Text>
              )}

              <Group justify="space-between">
                <Button variant="default" onClick={closeCheck}>{t('common.cancel')}</Button>
                <Group gap={8}>
                  <Button variant="light" color="orange" size="sm"
                    leftSection={<IconCheck size={14} />}
                    loading={savingProgress} onClick={handleSaveProgress}>
                    {t('checking.savePartial')}
                  </Button>
                  <Button color="teal" leftSection={<IconCheck size={15} />}
                    loading={submitting} onClick={handleSubmit}>
                    {t('checking.saveCheckResults', 'Save Check Results')}
                  </Button>
                </Group>
              </Group>
            </Stack>
          )
        })()}
      </Modal>

      {/* ── Cancel Session Modal ── */}
      <Modal opened={cancelOpened} onClose={closeCancel} size="sm" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t("checking.cancelDraftSession","Cancel Draft Session")}</Text>}>
        <Stack gap="md">
          <Box style={{ background: '#fef2f2', borderRadius: 8, padding: '10px 12px', border: '1px solid #fca5a5' }}>
            <Text size="sm" fw={600} c="red">⚠️ Confirm Cancellation?</Text>
            <Text size="xs" c="dimmed" mt={2}>
              Draft "{cancelTarget?.comset?.name}" will be deleted and cannot be recovered
            </Text>
          </Box>
          <Textarea
            label={t("checking.cancellationReason","Cancellation Reason")}
            placeholder={t("checking.cancelReasonPlaceholder","e.g. data error, restart, duplicate...")}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            minRows={2}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeCancel}>{t('checking.dontCancel', "Don't Cancel")}</Button>
            <Button color="red" disabled={!cancelReason.trim()}
              onClick={async () => {
                if (!cancelTarget) return
                try {
                  await cancelCheckSession(cancelTarget.id, cancelReason)
                  notifications.show({ color: 'teal', message: `Cancelled draft "${cancelTarget.comset?.name}"` })
                  closeCancel(); load()
                } catch (e: any) {
                  notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Cancellation failed' })
                }
              }}>
              {t('checking.confirmCancel', 'Confirm Cancel')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Lost Confirmation Modal ── */}
      <Modal opened={lostConfirmOpened} onClose={closeLostConfirm} size="md" radius="lg"
        title={
          <Group gap={8}>
            <Badge color="red" size="lg" variant="filled">⚠️</Badge>
            <Text fw={700} size="lg" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t("checking.assetsMarkedNotFound","Assets Marked as Not Found")}
            </Text>
          </Group>
        }>
        <Stack gap="md">
          <Text size="sm">
            There are <b>{lostAssets.length} asset(s)</b> marked as "Not Found" during this check:
          </Text>

          <Stack gap={6}>
            {lostAssets.map((r: any) => (
              <Box key={r.assetId} style={{ background: '#fef2f2', borderRadius: 8, padding: '8px 12px', border: '1px solid #fca5a5' }}>
                <Group gap={8}>
                  <Badge size="xs" color="red" variant="light">🔍 Not Found</Badge>
                  <Text size="sm" fw={600}>{r.asset?.assetCode ?? `#${r.assetId}`}</Text>
                  <Text size="xs" c="dimmed">{r.asset?.name}</Text>
                </Group>
                {r.note && <Text size="xs" c="dimmed" mt={2}>Note: {r.note}</Text>}
              </Box>
            ))}
          </Stack>

          <Box style={{ background: '#fff7ed', borderRadius: 8, padding: '10px 12px', border: '1px solid #fed7aa' }}>
            <Text size="sm" fw={600} c="orange" mb={4}>Do you want to report these assets as "Lost" in the system?</Text>
            <Text size="xs" c="dimmed">
              If you choose "Report Lost" → asset status changes to LOST with a movement log
              If you choose "Don't Report" → only the check result is saved, you can report Lost later from the Assets page
            </Text>
          </Box>

          <Group justify="flex-end" gap={8}>
            <Button variant="default" onClick={closeLostConfirm}>
              {t('checking.dontReportSaveOnly', "Don't Report — Save Check Only")}
            </Button>
            <Button color="red" leftSection={<IconAlertTriangle size={15} />}
              onClick={async () => {
                let successCount = 0
                for (const r of lostAssets) {
                  try {
                    await reportAssetLost(r.assetId, {
                      lostDate: new Date().toISOString().slice(0, 10),
                      lostNote: r.note || `Not found during COMSET check ${selectedComset?.name ?? ''} on ${new Date().toLocaleDateString('en-GB')}`,
                    })
                    successCount++
                  } catch { /* skip individual failures */ }
                }
                notifications.show({
                  color: 'red',
                  title: 'Lost Report Completed',
                  message: `${successCount}/${lostAssets.length} assets changed to LOST status`,
                })
                closeLostConfirm()
                load()
              }}>
              {t('checking.reportAllAsLost', { count: lostAssets.length })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
